import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatErr } from "../lib/api";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { COURSES, BATCHES } from "../lib/constants";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", password: "", role: "student",
    registration_number: "", roll_number: "", degree: "", batch: "",
  });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created");
      navigate("/households");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid place-items-center bg-brand-bg bg-grain px-4 py-12">
      <div className="w-full max-w-lg bg-white rounded-lg border border-brand-border p-8 shadow-tactile">
        <div className="flex items-center gap-2.5 mb-6">
          <img src="/img/acn-logo.png" alt="ACN" className="h-12 w-12 rounded-full object-cover bg-white border border-[#3B2A8C]/20" />
          <div className="leading-tight">
            <div className="font-heading font-bold text-[#3B2A8C] uppercase tracking-wide">Akal College of Nursing</div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-muted">Baru Sahib</div>
          </div>
        </div>
        <h2 className="font-heading text-3xl font-semibold">Create your account</h2>
        <p className="text-brand-muted mt-2 text-sm">Student registration — Faculty accounts are provisioned by the Manager.</p>

        <form onSubmit={submit} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="register-form">
          <Field label="Full name" testId="register-name" className="sm:col-span-2">
            <input className="input" required value={form.name} onChange={set("name")} data-testid="register-name-input" />
          </Field>
          <Field label="Email" testId="register-email">
            <input className="input" type="email" required value={form.email} onChange={set("email")} data-testid="register-email-input" />
          </Field>
          <Field label="Phone" testId="register-phone">
            <input className="input" type="tel" required value={form.phone} onChange={set("phone")} placeholder="10-digit phone" data-testid="register-phone-input" />
          </Field>
          <Field label="Course *" testId="register-course">
            <select className="input" required value={form.degree} onChange={set("degree")} data-testid="register-course-input">
              <option value="">Select course…</option>
              {COURSES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Batch (Joining Year) *" testId="register-batch">
            <select className="input" required value={form.batch} onChange={set("batch")} data-testid="register-batch-input">
              <option value="">Select joining year…</option>
              {BATCHES.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </Field>
          <Field label="Registration Number *" testId="register-regno">
            <input className="input" required value={form.registration_number} onChange={set("registration_number")} placeholder="ACN-2024-001" data-testid="register-regno-input" />
          </Field>
          <Field label="Roll Number" testId="register-roll">
            <input className="input" value={form.roll_number} onChange={set("roll_number")} data-testid="register-roll-input" />
          </Field>
          <Field label="Password" testId="register-password" className="sm:col-span-2">
            <div className="relative">
              <input className="input pr-12" type={showPw ? "text" : "password"} required minLength={6} value={form.password} onChange={set("password")} data-testid="register-password-input" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 grid place-items-center rounded hover:bg-brand-border/50" data-testid="toggle-register-password">
                {showPw ? <EyeOff className="h-4 w-4 text-brand-muted" /> : <Eye className="h-4 w-4 text-brand-muted" />}
              </button>
            </div>
          </Field>

          <button
            type="submit"
            disabled={loading}
            className="sm:col-span-2 w-full h-12 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white font-medium transition-colors disabled:opacity-60"
            data-testid="register-submit"
          >
            {loading ? "Creating…" : "Create student account"}
          </button>
        </form>

        <div className="mt-6 text-sm text-brand-muted text-center">
          Already a member?{" "}
          <Link to="/login" className="text-brand-primary font-medium hover:underline" data-testid="link-login">Sign in</Link>
        </div>
      </div>

      <style>{`.input { width: 100%; height: 3rem; padding: 0 0.75rem; border-radius: 0.375rem; background: #F2F0EB; border: 1px solid #E5E3DB; outline: none; transition: all 0.2s; }
      .input:focus { background: #fff; box-shadow: 0 0 0 2px #2B5F4E; }`}</style>
    </div>
  );
}

function Field({ label, children, testId, className = "" }) {
  return (
    <div data-testid={testId} className={className}>
      <label className="text-xs font-semibold uppercase tracking-wider text-brand-muted">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
